<script lang="ts">
  import { formatRelativeTime, getStatusLabel } from "./utils";

  export let title: string = "Opportunity Sync Status";
  export let opportunities: any[] = [];
  export let filterStatus: string = "all";
  
  export let columns: Array<{
    key: string;
    label: string;
    type?: 'text' | 'status' | 'boolean' | 'time';
    align?: 'left' | 'center' | 'right';
  }> = [];

  const defaultColumns: Array<{
    key: string;
    label: string;
    type?: 'text' | 'status' | 'boolean' | 'time';
    align?: 'left' | 'center' | 'right';
  }> = [
    { key: 'name', label: 'Opportunity Name', type: 'text' },
    { key: 'awsSynced', label: 'AWS', type: 'boolean', align: 'center' },
    { key: 'acelinkSynced', label: 'Acelink', type: 'boolean', align: 'center' },
    { key: 'status', label: 'Status', type: 'status' },
    { key: 'lastUpdate', label: 'Last Update', type: 'time' }
  ];

  $: activeColumns = columns && columns.length > 0 ? columns : defaultColumns;

  export let filterOptions: Array<{ value: string, label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'synced', label: 'Synced' },
    { value: 'pending', label: 'Pending' },
    { value: 'error', label: 'Error' },
    { value: 'conflict', label: 'Conflict' }
  ];

  export let labels: {
    emptyMessage?: string;
    untitled?: string;
  } = {};

  const defaultLabels = {
    emptyMessage: "No opportunities found",
    untitled: "Untitled"
  };

  $: activeLabels = { ...defaultLabels, ...labels };

  export let onSelectOpportunity: (opportunity: any) => void = () => {};

  const handleRowClick = (item: any) => onSelectOpportunity(item);
</script>

<div
  class="bg-[#24283a] border border-white/10 rounded-xl p-6"
>
  <div class="flex justify-between items-center mb-4">
    <h3
      class="text-[1.125rem] font-bold text-white m-0 uppercase tracking-[0.5px]"
    >
      {title}
    </h3>
    <select
      bind:value={filterStatus}
      class="px-4 py-2 bg-[#1b1e2e] border border-white/20 text-white rounded-md text-sm cursor-pointer font-medium focus:outline-none focus:border-white focus:shadow-[0_0_0_3px_rgba(255,255,255,0.1)]"
    >
      {#each filterOptions as option}
        <option value={option.value}>{option.label}</option>
      {/each}
    </select>
  </div>
  <div class="overflow-x-auto">
    <div class="min-w-full">
      <table class="w-full border-collapse">
        <thead>
          <tr class="bg-black/20">
            {#each activeColumns as col}
              <th
                class={`px-4 py-3 text-[0.813rem] font-bold text-white/90 uppercase tracking-[0.5px] border-b-2 border-white/10 ${
                  (col.align || 'left') === 'center'
                    ? 'text-center'
                    : (col.align || 'left') === 'right'
                    ? 'text-right'
                    : 'text-left'
                }`}
              >
                {col.label}
              </th>
            {/each}
          </tr>
        </thead>
        <tbody>
          {#if opportunities && opportunities.length > 0}
            {#each opportunities as item}
              <tr
                on:click={() => handleRowClick(item)}
                on:keydown={(e) => e.key === 'Enter' && handleRowClick(item)}
                class="border-b border-white/5 cursor-pointer transition-all hover:bg-white/5 focus:outline focus:outline-2 focus:outline-white focus:outline-offset-[-2px]"
                role="button"
                tabindex="0"
              >
                {#each activeColumns as col}
                  <td
                    class={`px-4 py-3 text-sm text-white/80 ${
                      (col.align || 'left') === 'center'
                        ? 'text-center'
                        : (col.align || 'left') === 'right'
                        ? 'text-right'
                        : 'text-left'
                    } ${col.key === 'name' ? 'font-semibold text-white' : ''}`}
                  >
                    {#if col.type === 'boolean'}
                      {item[col.key] ? "✅" : "❌"}
                    {:else if col.type === 'status'}
                      <span 
                        class={`inline-block px-3 py-1 rounded-2xl text-xs font-semibold whitespace-nowrap ${
                          item[col.key] === 'synced'
                            ? 'bg-green-500/20 text-green-500 border border-green-500'
                            : item[col.key] === 'not_synced'
                            ? 'bg-amber-500/20 text-amber-500 border border-amber-500'
                            : item[col.key] === 'error'
                            ? 'bg-red-500/20 text-red-500 border border-red-500'
                            : item[col.key] === 'conflict'
                            ? 'bg-amber-500/20 text-amber-500 border border-amber-500'
                            : ''
                        }`}
                      >
                        {getStatusLabel(item[col.key])}
                      </span>
                    {:else if col.type === 'time'}
                      <span class="text-white/50 text-xs">
                        {formatRelativeTime(item[col.key])}
                      </span>
                    {:else}
                      {item[col.key] || (col.key === 'name' ? activeLabels.untitled : "—")}
                    {/if}
                  </td>
                {/each}
              </tr>
            {/each}
          {:else}
            <tr>
              <td
                colspan={activeColumns.length}
                class="text-center text-white/50 py-12"
              >
                {activeLabels.emptyMessage}
              </td>
            </tr>
          {/if}
        </tbody>
      </table>
    </div>
  </div>
</div>
